import { Link } from "react-router-dom";
import { Check, X, ArrowRight, FileText, Search, AlertTriangle, Mail, Sparkles, MessageCircle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import SEO, { ORG_JSONLD, breadcrumb, serviceJsonLd } from "../components/SEO";

const MAIL_URL = "mailto:fmsubastasinvestment@gmail.com?subject=Consultoría%20de%20Deudas";
const WHATSAPP_MSG = WA_MSG.consultoria;

const HOW = [
  { icon: FileText, title: "Nos mandas la nota simple registral",
    desc: "Debe estar actualizada a fecha de hoy. La certificación de cargas publicada en el BOE no es suficiente ni fiable para este análisis." },
  { icon: Search, title: "Nuestro equipo la analiza con lupa",
    desc: "Revisamos cada carga, cada anotación, cada matiz. Detectamos cuáles se cancelan, cuáles sobreviven a la subasta y si hay indicios de riesgo adicional (embargos con mismo rango registral, hipotecas anteriores, ejecuciones no visibles en BOE…)." },
  { icon: AlertTriangle, title: "Te enviamos nuestras conclusiones",
    desc: "Claras y sin rodeos: si la subasta es segura, si tiene riesgos, o si directamente deberías descartarla. Entrega en 24-72 horas hábiles." },
];

const INCLUDED = [
  "Análisis registral profesional de la nota simple",
  "Qué cargas se cancelan y cuáles subsisten",
  "Detección de riesgos no visibles en el BOE",
  "Conclusiones por escrito, claras y prácticas",
];

const NOT_INCLUDED = [
  "Informe completo con valoración e ITP",
  "Cálculo de valor de mercado ni tramos de puja",
  "Acompañamiento el día de la subasta",
];

export default function Consultoria() {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Consultoría de Deudas · Análisis de Nota Simple 95 €"
        description="Análisis registral profesional de la nota simple en 24-72 horas. Detectamos cargas que se cancelan, cuáles subsisten y riesgos no visibles en el BOE. Informe escrito por 95 €."
        keywords="consultoría deudas subasta, análisis nota simple, cargas registrales subasta BOE, informe cargas vivienda subasta, certificación cargas BOE"
        path="/consultoria"
        jsonLd={[
          ORG_JSONLD,
          breadcrumb([{ name: "Inicio", path: "/" }, { name: "Consultoría de Deudas", path: "/consultoria" }]),
          serviceJsonLd({
            name: "Consultoría de Deudas · Análisis de Nota Simple",
            description: "Análisis registral profesional sobre cargas y deudas que afectan a un inmueble en subasta del BOE. Informe escrito en 24-72 horas hábiles. IVA incluido.",
            price: 95,
            path: "/consultoria",
          }),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="bg-[#2d1f1a] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#E0651F]/15 border border-[#E0651F]/40 text-[#E0651F] px-3 py-1 rounded-sm text-[10px] tracking-[0.25em] uppercase font-bold mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Para inversores con experiencia
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
              Consultoría de Deudas.<br /><span className="text-[#E0651F]">Para quienes ya saben jugar</span>… pero no quieren perder por un descuido.
            </h1>
            <p className="text-sm text-stone-400 leading-relaxed max-w-2xl mb-8">
              Si ya has participado en subastas del BOE, sabes hacer un depósito y te mueves con soltura por la plataforma, no necesitas un informe extenso ni acompañamiento el día D. Solo quieres saber una cosa: <strong className="text-white">si ese inmueble tiene cargas peligrosas</strong>. Para eso estamos nosotros.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/consultoria/contratar"
                className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-contratar">
                Contratar consultoría · 95 € <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={buildWaUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openWhatsApp(WHATSAPP_MSG); }}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> Tengo dudas antes
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-[#2d1f1a] p-8 rounded-sm border-l-4 border-[#E0651F] shadow-2xl">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Tarifa</div>
              <div className="mb-3">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-6xl font-black">95 €</span>
                  <span className="text-sm text-stone-500">/ informe · IVA incl.</span>
                </div>
                <div className="text-xs text-stone-500 mt-1">Pago único, sin permanencia ni suscripción.</div>
              </div>
              <div className="bg-stone-50 border border-stone-200 px-4 py-3 rounded-sm text-xs text-stone-700 font-mono space-y-1 mb-4" data-testid="iva-breakdown">
                <div className="flex justify-between"><span>Base imponible</span><span>78,51 €</span></div>
                <div className="flex justify-between"><span>IVA (21 %)</span><span>16,49 €</span></div>
                <div className="flex justify-between font-bold text-[#2d1f1a] pt-1 border-t border-stone-300"><span>Total</span><span>95,00 €</span></div>
              </div>
              <ul className="text-sm text-stone-700 space-y-2">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span>Conclusiones por escrito en <strong>24-72 horas</strong></span></li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span>Pago seguro Stripe · Tarjeta + Bizum</span></li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span>Factura con tus datos fiscales</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Cómo funciona</div>
          <h2 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a]">Tres pasos. Cero tecnicismos innecesarios.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {HOW.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white border border-stone-200 p-7 rounded-sm">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E0651F] mb-3">Paso {String(i + 1).padStart(2, "0")}</div>
                <Icon className="w-7 h-7 text-[#2d1f1a] mb-4" />
                <h3 className="font-display font-black text-lg text-[#2d1f1a] tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* INCLUDED / NOT INCLUDED */}
      <section className="bg-stone-100 py-20">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-stone-200 p-8 rounded-sm">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-emerald-700 mb-3">Qué SÍ incluye</div>
            <h3 className="font-display font-black text-2xl text-[#2d1f1a] tracking-tight mb-5">La pieza clave que te falta.</h3>
            <ul className="space-y-3">
              {INCLUDED.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-stone-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-[#2d1f1a] text-white p-8 rounded-sm">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Qué NO incluye</div>
            <h3 className="font-display font-black text-2xl tracking-tight mb-5">Porque ya lo sabes.</h3>
            <ul className="space-y-3">
              {NOT_INCLUDED.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-stone-300"><X className="w-4 h-4 text-stone-500 mt-0.5 flex-shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-stone-400 leading-relaxed">
              ¿Necesitas también valoración, cálculo de ITP y acompañamiento el día D? Mira el <Link to="/informe-premium" className="text-[#E0651F] font-bold hover:underline">Informe Premium</Link>.
            </div>
          </div>
        </div>
      </section>

      {/* FOR WHOM */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">¿Para quién es?</div>
        <h2 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-6 max-w-3xl leading-tight">
          Para usuarios con experiencia que quieren <span className="text-[#E0651F]">una segunda opinión profesional</span> antes de arriesgar.
        </h2>
        <p className="text-stone-600 text-base leading-relaxed max-w-2xl mb-8">
          Para los que no se dejan llevar por intuiciones, sino por información precisa. Evita errores caros con una revisión experta, ágil y directa. Mándanos tu nota simple actualizada y te diremos si esa subasta merece la pena o no.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/consultoria/contratar"
            className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
            data-testid="bottom-cta-contratar">
            Contratar consultoría · 95 € <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={MAIL_URL}
            className="inline-flex items-center gap-2 bg-white hover:bg-[#2d1f1a] hover:text-white text-[#2d1f1a] border border-stone-300 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
            <Mail className="w-4 h-4" /> Email
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
